// src/components/SideBar.jsx
// =============================================================================
// BLOCK SB.0 — Grouped Side Bar (parent/child + permissions)
// - Parent groups render only if they contain >= 1 permitted child item
// - Groups are collapsible (per-group open/closed state)
// - Preserves PageButtons, version fetch, and Alert behavior
// - Tight icon->text spacing + smaller child font so more text fits
// =============================================================================

import React from 'react';

import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StoreIcon from '@mui/icons-material/Store';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import PageContext from '../contexts/PageContext.jsx';
import PermissionsContext from '../contexts/PermissionsContext.jsx';
import Alert from './common/Alert.jsx';
import NavButton from './common/NavButton.jsx';

function SideBar({ onClick }) {
  const { PageButtons } = React.useContext(PageContext);
  const { permissions } = React.useContext(PermissionsContext);

  const [version, setVersion] = React.useState('');
  const [error, setError] = React.useState(null);

  // =============================================================================
  // BLOCK SB.1 — Groups definition (includes ALL original menu items)
  // - Move items between groups by relocating child objects below
  // - "Other" is a catch-all group so nothing gets lost
  // =============================================================================
  const groupsDefinition = React.useMemo(() => {
    return [
      // -------------------------
      // Sales
      // -------------------------
      {
        key: 'sales',
        name: 'Sales',
        Icon: <MonetizationOnIcon />,
        children: [
          // ORIGINAL: commissions
          {
            permission: 'commissions',
            component: (
              <NavButton
                Icon={<MonetizationOnIcon />}
                text="Commissions"
                to="/commissions"
                data-cy="commissions-nav"
              />
            ),
          },
          {
            permission: 'commissions',
            component: (
              <NavButton
                Icon={<MonetizationOnIcon />}
                text="Gross Commissions"
                to="/commgross"
                data-cy="commgross-nav"
              />
            ),
          },
          // ORIGINAL: prod-mgrs
          {
            permission: 'prod-mgrs',
            component: (
              <NavButton
                Icon={<MonetizationOnIcon />}
                text="Program Manager Comm"
                to="/prod-mgrs"
                data-cy="prod-mgrs-nav"
              />
            ),
          },
        ],
      },

      // -------------------------
      // Order Entry
      // -------------------------
      {
        key: 'orders',
        name: 'Orders',
        Icon: <ShoppingCartIcon />,
        children: [
          // ORIGINAL: order-entry
          {
            permission: 'order-entry',
            component: (
              <NavButton
                Icon={<StoreIcon />}
                text="Order Entry"
                to="/order-entry"
                data-cy="order-entry-nav"
              />
            ),
          },

          // ORIGINAL: outstanding-orders
          {
            permission: 'outstanding-orders',
            component: (
              <NavButton
                Icon={<ShoppingCartIcon />}
                text="Outstanding Orders"
                to="/outstanding-orders"
                data-cy="outstanding-orders-nav"
              />
            ),
          },
          {
            permission: 'products-on-back-order',
            component: (
              <NavButton
                Icon={<StoreIcon />}
                text="Products On Back Order"
                to="/products-on-back-order"
                data-cy="products-on-back-order-nav"
              />
            ),
          },
          // ORIGINAL: trackinginfo
          {
            permission: 'trackinginfo',
            component: (
              <NavButton
                Icon={<ShoppingCartIcon />}
                text="Shipping Tracking"
                to="/trackinginfo"
                data-cy="shipping-tracking-nav"
              />
            ),
          },
        ],
      },

      // -------------------------
      // TZMMS
      // -------------------------
      {
        key: 'tzmms',
        name: 'TZMMS',
        Icon: <AssessmentIcon />,
        children: [
          {
            permission: 'hr-report',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="HR Report"
                to="/hr-report"
                data-cy="hr-report-nav"
              />
            ),
          },
          {
            permission: 'ecg-viewer',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="ECG-VIEWER"
                to="/ecg-viewer"
                data-cy="ecg-viewer-nav"
              />
            ),
          },
          {
            permission: 'order-entry-tms',
            component: (
              <NavButton
                Icon={<StoreIcon />}
                text="Order Entry"
                to="/order-entry?company=tms"
                data-cy="order-entry-tms-nav"
              />
            ),
          },
          // NEW: order-requirements
          {
            permission: 'order-requirements',
            component: (
              <NavButton
                Icon={<StoreIcon />}
                text="Order Requirements"
                to="/order-requirements"
                data-cy="order-requirements-nav"
              />
            ),
          },
          // ORIGINAL: customer-summary
          {
            permission: 'customer-summary',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="Customer Summary"
                to="/customer-summary"
                data-cy="customer-summary-nav"
              />
            ),
          },
          {
            permission: 'tzmms-inv', // Match PAGE_PERMISSION_KEY in tzmmsInvUi.js.
            component: (
              <NavButton
                Icon={<StoreIcon />}
                text="TZMMS Inventory Counter"
                to="/tzmms-inv"
                data-cy="tzmms-inv-nav"
              />
            ),
          },
          {
            permission: 'tzmms-activity',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="TZMMS Activity"
                to="/tzmms-activity"
                data-cy="tzmms-activity-nav"
              />
            ),
          },
          {
            permission: 'outstanding-orders-tms',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="Outstanding Orders"
                to="/outstanding-orders-tms"
                data-cy="outstanding-orders-tms-nav"
              />
            ),
          },
          {
            permission: 'enrollments',
            component: (
              <NavButton
                Icon={<StoreIcon />}
                text="Enrollments"
                to="/enrollments"
                data-cy="enrollments-nav"
              />
            ),
          },
        ],
      },

      // -------------------------
      // Other (catch-all so nothing is lost)
      // - Put any items here if you don't know where they belong yet
      // -------------------------
      {
        key: 'other',
        name: 'Other',
        Icon: <ShoppingCartIcon />,
        children: [
          {
            permission: 'parse-invoice',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="Parse Invoice"
                to="/parse-invoice"
                data-cy="parse-invoice-nav"
              />
            ),
          },
          {
            permission: 'bank-posting',
            component: (
              <NavButton
                Icon={<MonetizationOnIcon />}
                text="Bank Posting"
                to="/bank-posting"
                data-cy="bank-posting-nav"
              />
            ),
          },
          {
            permission: 'inv-tracker',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="Inventory Tracker"
                to="/inv-tracker"
                data-cy="inv-tracker-nav"
              />
            ),
          },
          {
            permission: 'daily-summary',
            component: (
              <NavButton
                Icon={<AssessmentIcon />}
                text="Daily Summary"
                to="/daily-summary"
                data-cy="daily-summary-nav"
              />
            ),
          },
          // performance
          {
            permission: 'performance',
            component: (
              <NavButton
                Icon={<ShoppingCartIcon />}
                text="Territory Performance"
                to="/performance"
                data-cy="territory-performance-nav"
              />
            ),
          },
        ],
      },
    ];
  }, []);

  // =============================================================================
  // BLOCK SB.2 — Compute visible groups by permissions
  // =============================================================================
  const visibleGroups = React.useMemo(() => {
    return groupsDefinition
      .map((group) => {
        const allowedChildren = group.children.filter(({ permission }) =>
          permissions.includes(permission)
        );
        return { ...group, children: allowedChildren };
      })
      .filter((group) => group.children.length > 0);
  }, [groupsDefinition, permissions]);

  // =============================================================================
  // BLOCK SB.3 — Collapse/open state per group
  // =============================================================================
  const [openGroups, setOpenGroups] = React.useState(() =>
    visibleGroups.reduce((acc, g) => {
      acc[g.key] = false; // default collapsed
      return acc;
    }, {})
  );

  React.useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };

      visibleGroups.forEach((g) => {
        if (!(g.key in next)) next[g.key] = false;
      });

      Object.keys(next).forEach((k) => {
        if (!visibleGroups.find((g) => g.key === k)) delete next[k];
      });

      return next;
    });
  }, [visibleGroups]);

  function toggleGroup(key) {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  // =============================================================================
  // BLOCK SB.4 — Fetch app version (unchanged behavior)
  // =============================================================================
  const fetchVersion = React.useCallback(async () => {
    try {
      const versionNumber = await axios.get('/api/version');
      setVersion(versionNumber.data.version);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  React.useEffect(() => {
    fetchVersion();
  }, [fetchVersion]);

  // =============================================================================
  // BLOCK SB.5 — Render (100% width)
  // =============================================================================
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflowX: 'hidden',
      }}
    >
      <List onClick={onClick} data-cy="sidebar">
        {!!PageButtons && (
          <>
            {PageButtons}
            <Divider />
          </>
        )}

        {visibleGroups.map((group) => (
          <Box key={group.key}>
            {/* Parent row */}
            <ListItem disablePadding>
              <ListItemButton
                data-cy={`sidebar-group-${group.key}`}
                onClick={() => toggleGroup(group.key)}
                sx={{
                  py: 0.5,
                  minHeight: 36,
                  '& .MuiListItemIcon-root': {
                    minWidth: 28, // tighter icon->text spacing
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>{group.Icon}</ListItemIcon>
                <ListItemText
                  primary={group.name}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                />
                {openGroups[group.key] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            {/* Children */}
            <Collapse in={!!openGroups[group.key]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.children.map(({ component }, idx) => (
                  <ListItem key={`${group.key}-child-${idx}`} sx={{ pl: 4, py: 0 }}>
                    <Box
                      sx={{
                        width: '100%',
                        '& .MuiTypography-root': {
                          fontSize: '0.82rem', // smaller child font
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: 26,
                        },
                      }}
                    >
                      {component}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>

      {/* Version */}
      <Typography
        data-cy="version-number"
        sx={{
          paddingLeft: 2,
          position: 'absolute',
          bottom: 0,
          paddingBottom: 1,
          fontSize: '0.75rem',
          opacity: 0.7,
        }}
      >
        {version && `v${version}`}
      </Typography>

      {/* Error snackbar */}
      <Alert message={error} setMessage={setError} level="error" variant="snackbar" />
    </Box>
  );
}

export default SideBar;
